"use client"

import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Badge } from "@/components/ui/badge"
import { ArrowRight, ArrowLeft, Check, User, Building, Plus } from "lucide-react"
import { CompanyAutocomplete } from "./company-autocomplete"
import { toast } from "sonner"
import {
  getGetCompaniesJobContactCountsQueryKey,
  getGetCompaniesQueryKey,
  getGetContactsQueryKey,
  usePostCompanies,
  usePostContacts,
} from "openapi/api/endpoints"
import { useAuth } from "@/hooks/useAuth"
import { useQueryClient } from "@tanstack/react-query"
import { getFriendlyContactCreateError } from "@/utils/contact-errors"

type ContactType = "candidate" | "client" | "hiring_manager"

interface ContactFormData {
  // Step 1: Basic Info
  name: string
  email: string
  type: ContactType
  position: string
  phone: string
  location: string
  notes: string
  linkedinUrl: string

  // Step 2: Company
  company: string
  company_id: string
}

interface CompanyFormData {
  name: string
  website: string
  industry: string
  size: string
  location: string
  description: string
}

interface CreateContactWizardProps {
  onComplete?: (data: ContactFormData) => void
  onCancel?: () => void
  initialCompanyId?: string
  initialCompanyName?: string
}

export function CreateContactWizard({
  onComplete,
  onCancel,
  initialCompanyId,
  initialCompanyName,
}: CreateContactWizardProps) {
  const [currentStep, setCurrentStep] = useState(1)
  const [companyMode, setCompanyMode] = useState<"select" | "create">("select")
  const { user } = useAuth()
  const { mutateAsync: createContact } = usePostContacts()
  const { mutateAsync: createCompany, isPending: isCreatingCompany } = usePostCompanies()
  const queryClient = useQueryClient()

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ContactFormData>({
    defaultValues: {
      name: "",
      email: "",
      type: "hiring_manager",
      position: "",
      phone: "",
      location: "",
      notes: "",
      linkedinUrl: "",
      company: "",
      company_id: "",
    },
  })

  const companyForm = useForm<CompanyFormData>({
    defaultValues: {
      name: "",
      website: "",
      industry: "",
      size: "",
      location: "",
      description: "",
    },
  })

  const formData = watch()
  const hasPresetCompany = !!initialCompanyId
  const totalSteps = hasPresetCompany ? 1 : 2
  const steps = hasPresetCompany ? [1] : [1, 2]

  useEffect(() => {
    if (initialCompanyId) {
      setValue("company_id", initialCompanyId)
      setValue("company", initialCompanyName ?? "")
    }
  }, [initialCompanyId, initialCompanyName, setValue])

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1)
    }
  }

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }

  const onSubmit = async(data: ContactFormData) => {
    const selectedCompanyId = data.company_id || initialCompanyId || ""

    if (!selectedCompanyId) {
      toast.error("Company required", {
        description: "Select or create a company before creating this contact.",
        className: "text-white",
        descriptionClassName: "text-white",
        action: {
          label: "Dismiss",
          onClick: () => {},
        },
        style: {
          background: "#dc2626",
        },
      })
      return
    }

    try{
        await createContact({data:{
            first_name: data.name,
            last_name: '',
            email: data.email??'',
            contact_type: data.type??'hiring_manager',
            position: data.position??'',
            phone: data.phone??'',
            location: data.location??'',
            notes: data.notes??'',
            linkedinUrl: data.linkedinUrl??'',
            company_id: selectedCompanyId,
            created_by: user?.id??''
        }})

        await Promise.all([
          queryClient.invalidateQueries({ queryKey: getGetContactsQueryKey(), exact: false }),
          queryClient.invalidateQueries({ queryKey: getGetContactsQueryKey({ companyId: selectedCompanyId }), exact: false }),
          queryClient.invalidateQueries({ queryKey: getGetCompaniesJobContactCountsQueryKey(), exact: false }),
        ])

        if (onComplete) {
          onComplete(data)
        }

        toast.success("Added!",{
            description: 'New contact added successfully',
            action: {
            label: "Dismiss",
            onClick: () => {},
            },
            style: {
                background: '#a3e635'
            }
        })
    }catch(err){
      toast.error("An error occured!",{
        description: getFriendlyContactCreateError(err),
        className: "text-white",
        descriptionClassName: "text-white",
        action: {
          label: "Dismiss",
          onClick: () => {},
        },
        style: {
          background: "#dc2626",
        },
      });
    }
  }

  const handleCreateCompany = (companyName: string) => {
    companyForm.setValue("name", companyName)
    setCompanyMode("create")
  }

  const handleCompanyCreated = async(data: CompanyFormData) => {
    try{
        const createdCompany = await createCompany({data:{
            name: data.name,
            website: data.website || "",
            industry: data.industry || "",
            size: data.size || "",
            location: data.location || "",
            description: data.description || ""
        }})

        setValue("company", createdCompany.name, { shouldDirty: true, shouldValidate: true })
        setValue("company_id", createdCompany.id, { shouldDirty: true, shouldValidate: true })

        await Promise.all([
          queryClient.invalidateQueries({ queryKey: getGetCompaniesQueryKey(), exact: false }),
          queryClient.invalidateQueries({ queryKey: getGetCompaniesJobContactCountsQueryKey(), exact: false }),
        ])

        toast.success("Created!",{
            description: 'New company created successfully',
            action: {
            label: "Dismiss",
            onClick: () => {},
            },
            style: {
                background: '#a3e635'
            }
        })
        setCompanyMode("select")
        companyForm.reset()
    }catch(err){
      toast.error("An error occured!",{
        description: `${err}`,
        className: "text-white",
        action: {
          label: "Dismiss",
          onClick: () => {},
        },
        style: {
          background: "#dc2626",
        },
      });
    }
  }

  const handleCancelCompanyCreation = () => {
    setCompanyMode("select")
    companyForm.reset()
  }

  const isStepValid = (step: number): boolean => {
    switch (step) {
      case 1:
        return !!(formData.name && formData.email && formData.type)
      case 2:
        return !!formData.company
      default:
        return false
    }
  }

  return (
    <div className="space-y-6">
      {/* Progress Indicator */}
      <div className="flex items-center w-full">
        {steps.map((step, index) => (
          <div key={step} className="flex items-center flex-1">
            <div className="flex items-center gap-2 min-w-0">
              <div
                className={`flex items-center justify-center h-8 w-8 rounded-full border-2 transition-colors flex-shrink-0 ${
                  step < currentStep
                    ? "bg-primary border-primary text-primary-foreground"
                    : step === currentStep
                      ? "border-primary text-brand-ink"
                      : "border-muted text-muted-foreground"
                }`}
              >
                {step < currentStep ? <Check className="h-4 w-4" /> : step}
              </div>
              <span
                className={`text-sm font-medium whitespace-nowrap ${step === currentStep ? "text-foreground" : "text-muted-foreground"}`}
              >
                {step === 1 ? "Basic Info" : "Company"}
              </span>
            </div>
            {index < steps.length - 1 && <div className="flex-1 h-0.5 bg-muted mx-4" />}
          </div>
        ))}
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Step 1: Basic Contact Info */}
        {currentStep === 1 && (
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <User className="h-5 w-5 text-brand-ink" />
                <CardTitle>Basic Contact Information</CardTitle>
              </div>
              <CardDescription>Enter the essential details about this contact</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">
                    Full Name <span className="text-red-500">*</span>
                  </Label>
                  <Input id="name" placeholder="John Doe" {...register("name", { required: "Name is required" })} />
                  {errors.name && <p className="text-sm text-red-500">{errors.name.message}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">
                    Email <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="john@example.com"
                    {...register("email", {
                      required: "Email is required",
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: "Invalid email address",
                      },
                    })}
                  />
                  {errors.email && <p className="text-sm text-red-500">{errors.email.message}</p>}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="type">
                    Contact Type <span className="text-red-500">*</span>
                  </Label>
                  <Select value={formData.type} onValueChange={(value: ContactType) => setValue("type", value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hiring_manager">Hiring Manager</SelectItem>
                      {/* <SelectItem value="client">Client</SelectItem>
                      <SelectItem value="candidate">Candidate</SelectItem> */}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="position">Position</Label>
                  <Input id="position" placeholder="Software Engineer" {...register("position")} />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input id="phone" placeholder="+1 (555) 123-4567" {...register("phone")} />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="location">Location</Label>
                  <Input id="location" placeholder="San Francisco, CA" {...register("location")} />
                </div>
              </div>

              {formData.type === "candidate" && (
                <div className="space-y-2">
                  <Label htmlFor="linkedinUrl">LinkedIn URL</Label>
                  <Input
                    id="linkedinUrl"
                    type="url"
                    placeholder="https://linkedin.com/in/username"
                    {...register("linkedinUrl")}
                  />
                  <p className="text-xs text-muted-foreground">
                    System will resolve personal data from LinkedIn profile
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  placeholder="Add any additional notes about this contact..."
                  rows={3}
                  {...register("notes")}
                />
              </div>

              <div className="flex items-center gap-2 p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-300">
                  {formData.type.replace("_", " ")}
                </Badge>
                <span className="text-sm text-muted-foreground">
                  {formData.type === "candidate"
                    ? "This contact will be added to your talent pool"
                    : formData.type === "client"
                      ? "This contact represents a client organization"
                      : "This contact is a hiring decision maker"}
                </span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Company Assignment */}
        {currentStep === 2 && !hasPresetCompany && (
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Building className="h-5 w-5 text-brand-ink" />
                <CardTitle>Company Assignment</CardTitle>
              </div>
              <CardDescription>Select an existing company or create a new one</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {companyMode === "select" ? (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="company">
                      Company <span className="text-red-500">*</span>
                    </Label>
                    <CompanyAutocomplete
                      value={formData.company}
                      onChange={(name, id) => {
                        setValue("company", name)
                        setValue("company_id", id ?? "")
                      }}
                      onCreateCompany={handleCreateCompany}
                      placeholder="Start typing company name..."
                    />
                    <p className="text-sm text-muted-foreground">
                      Type to search existing companies or create a new one
                    </p>
                  </div>

                  {formData.company && (
                    <div className="p-4 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-800">
                      <div className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-green-600" />
                        <span className="font-medium text-green-800 dark:text-green-400">Company Selected</span>
                      </div>
                      <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                        {formData.name} will be associated with {formData.company}
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="space-y-4 p-4 bg-muted/50 rounded-lg border-2 border-dashed">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Plus className="h-4 w-4 text-brand-ink" />
                        <h4 className="font-medium">Create New Company</h4>
                      </div>
                      <Button type="button" variant="ghost" size="sm" onClick={handleCancelCompanyCreation} disabled={isCreatingCompany}>
                        Back to Search
                      </Button>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="companyName">
                        Company Name <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="companyName"
                        placeholder="Acme Corporation"
                        {...companyForm.register("name", { required: "Company name is required" })}
                      />
                      {companyForm.formState.errors.name && (
                        <p className="text-sm text-red-500">{companyForm.formState.errors.name.message}</p>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="industry">Industry</Label>
                        <Input id="industry" placeholder="e.g., Technology" {...companyForm.register("industry")} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="companySize">Company Size</Label>
                        <Select
                          value={companyForm.watch("size")}
                          onValueChange={(value) => companyForm.setValue("size", value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select size" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1-10">1-10 employees</SelectItem>
                            <SelectItem value="11-50">11-50 employees</SelectItem>
                            <SelectItem value="51-200">51-200 employees</SelectItem>
                            <SelectItem value="201-500">201-500 employees</SelectItem>
                            <SelectItem value="501-1000">501-1000 employees</SelectItem>
                            <SelectItem value="1000+">1000+ employees</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="companyLocation">Location</Label>
                        <Input
                          id="companyLocation"
                          placeholder="e.g., San Francisco, CA"
                          {...companyForm.register("location")}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="website">Website</Label>
                        <Input
                          id="website"
                          type="url"
                          placeholder="https://company.com"
                          {...companyForm.register("website")}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="description">Description</Label>
                      <Textarea
                        id="description"
                        placeholder="Brief description of the company..."
                        rows={3}
                        {...companyForm.register("description")}
                      />
                    </div>

                    <div className="flex justify-end gap-2 pt-2">
                      <Button type="button" variant="outline" onClick={handleCancelCompanyCreation} disabled={isCreatingCompany}>
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        onClick={companyForm.handleSubmit(handleCompanyCreated)}
                        disabled={isCreatingCompany || !companyForm.watch("name")?.trim()}
                      >
                        <Check className="h-4 w-4 mr-2" />
                        {isCreatingCompany ? "Creating..." : "Create Company"}
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Navigation Buttons */}
        <div className="flex justify-between">
          <div>
            {currentStep > 1 && (
              <Button type="button" variant="outline" onClick={handleBack}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
            )}
          </div>

          <div className="flex gap-2">
            {onCancel && (
              <Button type="button" variant="ghost" onClick={onCancel}>
                Cancel
              </Button>
            )}

            {currentStep < totalSteps ? (
              <Button type="button" onClick={handleNext} disabled={!isStepValid(currentStep)}>
                Next
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <Button type="submit" disabled={!isStepValid(currentStep)}>
                <Check className="h-4 w-4 mr-2" />
                Create Contact
              </Button>
            )}
          </div>
        </div>
      </form>
    </div>
  )
}
